import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BookmarkX } from "lucide-react";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { listBookmarks, removeBookmark } from "../lib/api";

export default function AccountPage() {
  const { isAuthenticated, isAdmin, user, signOut, loading } = useAuth();
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [bLoading, setBLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      navigate("/signin", { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let mounted = true;
    listBookmarks()
      .then((bs) => mounted && setBookmarks(bs))
      .catch((e) => mounted && setErr(e?.response?.data?.detail || e.message))
      .finally(() => mounted && setBLoading(false));
    return () => { mounted = false; };
  }, [isAuthenticated]);

  const handleRemove = async (problem_id) => {
    await removeBookmark(problem_id);
    setBookmarks((prev) => prev.filter((b) => b.problem_id !== problem_id));
  };

  if (!isAuthenticated) return null;

  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-12 pb-10 border-b border-ink-200" data-testid="account-page">
        <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
          Section 05 · Account
        </p>
        <h1 className="mt-3 font-heading text-3xl md:text-4xl font-medium text-ink-900 tracking-[-0.02em]">
          {user?.email}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
          <span className="font-mono text-[10px] uppercase tracking-wide-2 text-ink-500">
            Member · Unlimited access
          </span>
          {isAdmin && (
            <span className="font-mono text-[10px] uppercase tracking-wide-2 text-vermilion border border-vermilion/40 px-2 py-1" data-testid="admin-badge">
              Admin
            </span>
          )}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          {isAdmin && (
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-ink-900 text-white font-mono text-[11px] uppercase tracking-wide-2 hover:bg-ink-800 transition-colors"
              data-testid="admin-panel-link"
            >
              Admin panel
            </Link>
          )}
          <button
            onClick={async () => { await signOut(); navigate("/signin", { replace: true }); }}
            data-testid="signout-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-bone border border-ink-300 text-ink-900 font-mono text-[11px] uppercase tracking-wide-2 hover:bg-ink-100 transition-colors"
          >
            Sign out
          </button>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-14">
        <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
          Saved · {bookmarks.length}
        </p>
        <h2 className="mt-2 font-heading text-2xl md:text-3xl font-medium text-ink-900 tracking-tight">
          Bookmarked frameworks.
        </h2>

        {err && (
          <p className="mt-6 text-sm text-ink-600">{String(err)}</p>
        )}

        <div className="mt-8" data-testid="bookmarks-list">
          {bLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="skeleton h-16 w-full" />
              ))}
            </div>
          ) : bookmarks.length === 0 ? (
            <div className="border border-ink-200 bg-bone p-10 text-center">
              <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
                No bookmarks yet
              </p>
              <p className="mt-3 font-heading text-xl text-ink-900">
                Save problems to build your personal reference.
              </p>
              <Link to="/" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-ink-900 text-white font-mono text-[11px] uppercase tracking-wide-2">
                Browse directory →
              </Link>
            </div>
          ) : (
            <div className="grid gap-px bg-ink-200 border border-ink-200">
              {bookmarks.map((b) => (
                <div key={b.bookmark_id} className="bg-bone flex items-center" data-testid={`bookmark-${b.problem_id}`}>
                  <Link
                    to={`/${b.industry_slug}/${b.problem_slug}`}
                    className="flex-1 p-5 hover:bg-ink-50 transition-colors group flex items-center gap-4"
                  >
                    <div className="flex-1">
                      <p className="font-mono text-[10px] uppercase tracking-wide-2 text-ink-500">
                        {b.industry_name}
                      </p>
                      <h3 className="mt-1 font-heading text-base font-medium text-ink-900 leading-snug">
                        {b.problem_title}
                      </h3>
                    </div>
                    <ArrowRight size={14} className="text-ink-400 group-hover:text-ink-900 group-hover:translate-x-1 transition-all" />
                  </Link>
                  <button
                    onClick={() => handleRemove(b.problem_id)}
                    className="px-5 py-5 text-ink-400 hover:text-ink-900 transition-colors border-l border-ink-200"
                    data-testid={`remove-bookmark-${b.problem_id}`}
                    title="Remove bookmark"
                  >
                    <BookmarkX size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
